import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Descriptions, Modal, Progress, Space, Statistic, Steps, Tag, Typography, message } from 'antd';
import { CheckCircleFilled, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';

const ACTIVE_STATUSES = new Set(['queued', 'running']);

const STATUS_TAG = {
  queued: { color: 'processing', label: 'Queued' },
  running: { color: 'processing', label: 'Running' },
  completed: { color: 'success', label: 'Completed' },
  failed: { color: 'error', label: 'Failed' },
};

const REASON_LABELS = {
  manual_systems_review: 'Manual (Systems Review)',
  product_mapping_completion: 'Product Mapping Completion',
  manual: 'Manual',
};

// Mirrors the `phase` values the backend writes to the same import-state
// record it already polls (see backend/api/ahri.py `_set_solr_state_phase`)
// — three real stages of work, not one opaque "running" blob.
const PHASES = [
  { key: 'metrics', title: 'Certificate Metrics', unit: 'batches', description: 'Recomputing per-system warehouse availability' },
  { key: 'variants', title: 'Variant Availability', unit: 'variants', description: 'Refreshing indoor/outdoor/furnace combinations' },
  { key: 'solr_import', title: 'Publish to Solr', unit: 'documents', description: 'Writing refreshed availability to the search index' },
];
const PHASE_INDEX = PHASES.reduce((acc, p, i) => ({ ...acc, [p.key]: i }), {});

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatCount = (value) => Number(value || 0).toLocaleString();

const AhriInventoryRecompute = () => {
  const { getAuthHeaders } = useAuth();
  const [importState, setImportState] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const prevStatusRef = useRef('');

  const status = String(importState?.status || '').toLowerCase();
  const isActive = ACTIVE_STATUSES.has(status);

  const loadState = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/import/state`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to load import state'));
      setImportState(data?.state || null);
      setError('');
    } catch (err) {
      setError(String(err?.message || err));
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  useEffect(() => {
    if (!isActive) return undefined;
    // 1.5s while a phase is actively ticking (feels responsive without
    // hammering the endpoint); the backend now updates this state on every
    // metrics/variant/solr batch, so there's always something new to show.
    const id = setInterval(loadState, 1500);
    return () => clearInterval(id);
  }, [isActive, loadState]);

  useEffect(() => {
    const prev = prevStatusRef.current;
    if (prev && ACTIVE_STATUSES.has(prev) && status === 'completed') {
      message.success('Inventory recompute and AHRI Solr full import completed.');
    }
    if (prev && ACTIVE_STATUSES.has(prev) && status === 'failed') {
      message.error('Inventory recompute / Solr full import failed. Check the import details.');
    }
    prevStatusRef.current = status;
  }, [status]);

  const triggerRecompute = useCallback(async () => {
    setTriggering(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/ahri/inventory/recompute`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data?.detail || 'Failed to start inventory recompute'));
      if (data?.already_running) {
        message.info('An AHRI Solr import is already running. The current job will continue.');
      } else {
        message.success('Inventory recompute and AHRI Solr full import started.');
      }
      loadState();
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setTriggering(false);
    }
  }, [getAuthHeaders, loadState]);

  const confirmTrigger = useCallback(() => {
    Modal.confirm({
      title: 'Recompute inventory and run full import?',
      content:
        'This runs in three stages — certificate metrics, variant availability, then Solr publish — and may take several minutes on large catalogs. You\'ll see live progress for each stage.',
      okText: 'Recompute & Full Import',
      cancelText: 'Cancel',
      onOk: triggerRecompute,
    });
  }, [triggerRecompute]);

  const statusTag = useMemo(() => {
    const meta = STATUS_TAG[status] || { color: 'default', label: importState?.status || 'Idle' };
    return <Tag color={meta.color}>{meta.label}</Tag>;
  }, [status, importState]);

  const phase = String(importState?.phase || '').toLowerCase();
  const phaseIndex = PHASE_INDEX[phase];
  const hasPhaseInfo = phase === 'queued' || phaseIndex !== undefined;
  const phaseProcessed = Number(importState?.phase_processed || 0);
  const phaseTotal = Number(importState?.phase_total || 0);
  const phasePct = phaseTotal > 0 ? Math.min(100, Math.round((phaseProcessed / phaseTotal) * 100)) : null;

  const currentStepIndex = useMemo(() => {
    if (status === 'completed') return PHASES.length;
    if (phaseIndex !== undefined) return phaseIndex;
    return isActive ? 0 : -1;
  }, [status, phaseIndex, isActive]);

  const processed = Number(importState?.processed || 0);
  const total = Number(importState?.total || 0);
  const docCounts = importState?.doc_counts || {};

  const buttonLabel = useMemo(() => {
    if (!isActive) return 'Recompute Inventory & Full Import';
    if (phaseIndex !== undefined) {
      const def = PHASES[phaseIndex];
      return phasePct != null ? `${def.title}… ${phasePct}%` : `${def.title}…`;
    }
    return 'Starting…';
  }, [isActive, phaseIndex, phasePct]);

  return (
    <div className="row g-3">
      <div className="col-12">
        <Alert
          type="info"
          showIcon
          message="Inventory recompute & AHRI Solr full import"
          description="Recompute warehouse availability metrics for all AHRI systems and publish them to Solr via a full import. This also runs automatically when product mapping completes."
        />
      </div>

      {error && (
        <div className="col-12">
          <Alert type="error" showIcon message={error} />
        </div>
      )}

      <div className="col-12">
        <Card variant="borderless">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
            <div>
              <Typography.Text strong>Status </Typography.Text>
              {statusTag}
              {importState?.reason && (
                <Tag className="ms-1" color="blue">
                  {REASON_LABELS[importState.reason] || importState.reason}
                </Tag>
              )}
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={loadState} disabled={triggering}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<SyncOutlined spin={isActive} />}
                loading={triggering}
                disabled={isActive}
                onClick={confirmTrigger}
              >
                {buttonLabel}
              </Button>
            </Space>
          </div>

          {isActive && hasPhaseInfo && (
            <div className="mt-4">
              <Steps
                size="small"
                current={currentStepIndex}
                items={PHASES.map((p, i) => ({
                  title: p.title,
                  description: i === phaseIndex ? p.description : undefined,
                  icon: i < currentStepIndex ? <CheckCircleFilled /> : undefined,
                }))}
              />
              <div className="mt-3">
                {phaseIndex !== undefined ? (
                  <>
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>{PHASES[phaseIndex].description}</span>
                      <span>
                        {phaseTotal > 0
                          ? `${formatCount(phaseProcessed)} / ${formatCount(phaseTotal)} ${PHASES[phaseIndex].unit}`
                          : 'Counting rows…'}
                      </span>
                    </div>
                    <Progress
                      percent={phasePct ?? 100}
                      status="active"
                      showInfo={false}
                      strokeColor={phasePct != null ? undefined : '#d9d9d9'}
                    />
                  </>
                ) : (
                  <Typography.Text type="secondary" className="small">
                    Queued — will start recomputing certificate metrics shortly.
                  </Typography.Text>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="col-12 col-md-4">
        <Card size="small" variant="borderless">
          <Statistic title="Documents Processed" value={processed} suffix={total ? `/ ${total}` : ''} />
        </Card>
      </div>
      <div className="col-12 col-md-4">
        <Card size="small" variant="borderless">
          <Statistic title="AHRI Docs In Solr" value={Number(docCounts?.ahri || docCounts?.total || 0)} />
        </Card>
      </div>
      <div className="col-12 col-md-4">
        <Card size="small" variant="borderless">
          <Statistic title="Mode" value={String(importState?.mode || 'full').toUpperCase()} />
        </Card>
      </div>

      <div className="col-12">
        <Card size="small" variant="borderless" title="Last Import Details">
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
            <Descriptions.Item label="Job ID">{importState?.import_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Queued At">{formatDateTime(importState?.queued_at)}</Descriptions.Item>
            <Descriptions.Item label="Started At">{formatDateTime(importState?.started_at)}</Descriptions.Item>
            <Descriptions.Item label="Finished At">{formatDateTime(importState?.finished_at)}</Descriptions.Item>
            <Descriptions.Item label="Last Synced">{formatDateTime(importState?.last_synced_at)}</Descriptions.Item>
            <Descriptions.Item label="Documents Indexed">{Number(importState?.documents_indexed || 0)}</Descriptions.Item>
            {importState?.error && (
              <Descriptions.Item label="Error" span={3}>
                <Typography.Text type="danger">{importState.error}</Typography.Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      </div>
    </div>
  );
};

export default AhriInventoryRecompute;
